/**
 * Supabase client + capability check.
 *
 * Cloud sync is OPTIONAL. When VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY
 * are both set, the client connects and anonymous auth signs in automatically.
 * When blank, `isSupabaseEnabled()` returns false and every call site skips
 * the network path — the app stays local-first (Dexie only).
 *
 * Fill in the values in `.env` (see `.env.example`) to enable cloud sync.
 * The schema lives in `supabase/schema.sql` and is applied via the Supabase
 * dashboard or `supabase db push`.
 */

import { createClient, type SupabaseClient } from "@supabase/supabase-js";

const url = (import.meta.env.VITE_SUPABASE_URL as string | undefined)?.trim() ?? "";
const anonKey = (import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined)?.trim() ?? "";

export const isSupabaseEnabled = (): boolean => Boolean(url) && Boolean(anonKey);

/**
 * Every Supabase request through this client is bounded by a timeout. The
 * cloud mirror (desktop↔web sync) proved why this is non-negotiable: one
 * request that never resolves wedged the entire mirror pass forever — no
 * error, no retry, no map write — which froze sync for days and let the
 * duplicate storm build. A timed-out request REJECTS (AbortError), the
 * mirror's per-step try/catch logs it, and the pass moves on and completes.
 * Chained with any caller-provided signal so explicit aborts still work.
 */
export function fetchWithTimeout(
  input: RequestInfo | URL,
  init?: RequestInit,
  timeoutMs = 30_000,
): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  const signal = init?.signal;
  if (signal) {
    if (signal.aborted) controller.abort();
    else signal.addEventListener("abort", () => controller.abort(), { once: true });
  }
  return fetch(input, { ...init, signal: controller.signal }).finally(() => clearTimeout(timer));
}

/** The localStorage key supabase-js persists the session under:
 *  `sb-<project-ref>-auth-token` (e.g. sb-hadwloryxbujuhugstyq-auth-token). */
function authStorageKey(): string {
  try {
    const host = new URL(url).hostname;
    return `sb-${host.split(".")[0]}-auth-token`;
  } catch {
    return "";
  }
}

// ── Boot session rescue ────────────────────────────────────────────────────
// supabase-js destroys the persisted session when an auto-refresh FAILS
// (invalid_grant on a stale refresh token, or the PGRST303 clock-skew window
// minting tokens "issued at future"). That is the "the app reloaded and I'm
// logged out again" bug: a reload re-inits the client, the expired token
// auto-refreshes, the refresh fails, and the session is wiped from localStorage
// before the boot gate ever reads it. The rescue backs up the stored session
// before client creation and, if it disappears within the first seconds of
// boot, restores it — the user stays signed in and the request layer's skew
// self-heal (marketplace.ts) replays/refreshes on demand. An explicit sign-out
// cancels the rescue so it can never resurrect a session the user closed.
let bootRescue: { key: string; value: string; timer: number } | null = null;

function armBootRescue(key: string, value: string): void {
  if (typeof window === "undefined") return;
  bootRescue = {
    key,
    value,
    timer: window.setTimeout(() => {
      if (localStorage.getItem(key) === null) {
        localStorage.setItem(key, value);
        console.warn("[supabase] restored stored session after a failed boot refresh");
      }
      bootRescue = null;
    }, 2_000),
  };
}

export function cancelBootRescue(): void {
  if (bootRescue) {
    window.clearTimeout(bootRescue.timer);
    bootRescue = null;
  }
}

let _client: SupabaseClient | null = null;
let _clientPromise: Promise<SupabaseClient | null> | null = null;

export function getSupabase(): SupabaseClient | null {
  if (!isSupabaseEnabled()) return null;
  if (_client) return _client;
  // Back up the persisted session BEFORE the client is created: its boot
  // auto-refresh can destroy it on a failed refresh (see armBootRescue).
  const storedKey = authStorageKey();
  const storedBefore = storedKey && typeof localStorage !== "undefined" ? localStorage.getItem(storedKey) : null;
  _client = createClient(url, anonKey, {
    global: {
      // One wrapping point covers every REST call the mirror makes (batched
      // inserts/updates/deletes, pulls, item upserts) — see fetchWithTimeout.
      fetch: fetchWithTimeout,
    },
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: false,
      // PKCE — required for the desktop flow: signInWithOAuth generates a
      // code challenge + verifier (stored in this renderer's storage) and
      // returns the authorize URL without redirecting the window. After the
      // browser dance, exchangeCodeForSession reads the verifier back and
      // trades the code for a session.
      flowType: "pkce",
    },
  });
  // NOTE: deliberately NO startup refreshSession() here. The "JWT issued at
  // future" (PGRST303) bug is Supabase's server-side clock skew — GoTrue
  // mints under a clock >30s ahead of PostgREST, so ONLY freshly-minted
  // tokens fail. Refreshing mints a NEW token under the same skewed clock
  // (straight back into the failure window) and, when the stored refresh
  // token is stale, supabase-js SIGNS THE USER OUT. The durable fix lives in
  // marketplace.ts: on PGRST303, wait for the current token to age past the
  // skew and replay the same request.
  // Boot session rescue — if the client's auto-refresh deletes the stored
  // session within the first seconds of boot (a failed refresh, see above),
  // restore the backup so a reload never silently logs the user out.
  if (storedKey && storedBefore) armBootRescue(storedKey, storedBefore);
  return _client;
}

/**
 * Async version — signs in anonymously on first call so any downstream
 * query that needs `auth.uid()` works without the user having to log in.
 * Returns null if Supabase is disabled.
 */
export async function getSupabaseReady(): Promise<SupabaseClient | null> {
  if (!isSupabaseEnabled()) return null;
  if (_clientPromise) return _clientPromise;
  _clientPromise = (async () => {
    const client = getSupabase();
    if (!client) return null;
    const { data } = await client.auth.getSession();
    // NOTE: no refresh here — see the PGRST303 note in getSupabase(). A
    // refresh mints a fresh token under Supabase's skewed clock (right back
    // in the "issued at future" failure window) and can sign the user out
    // when the stored refresh token is stale. The clock-skew self-heal lives
    // in marketplace.ts (wait for the token to age, then replay).
    if (!data.session) {
      const { error } = await client.auth.signInAnonymously();
      if (error) {
        console.warn("[supabase] anonymous sign-in failed:", error.message);
      }
    }
    return client;
  })();
  return _clientPromise;
}

/** Tear down the cached client (used on logout or settings change). */
export function resetSupabase(): void {
  _client = null;
  _clientPromise = null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Auth — desktop login (custom-scheme deep link)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * URL the system browser redirects to after a successful Supabase sign-in.
 * This is a LOOPBACK URL served by a tiny HTTP server in the main process
 * (main/services/auth-callback-server.ts, fixed port 45123) — the desktop-
 * standard pattern that works in dev AND packaged builds. A custom scheme
 * (portals://) would prompt "Open Electron?" and spawn a NEW instance in
 * dev, hanging the browser tab; the loopback server catches the callback
 * in-process and forwards it to the renderer.
 *
 * NOTE: this URL must be added to Supabase → Authentication → URL
 * Configuration → Redirect URLs, or the provider refuses the redirect.
 *
 * On the WEB build (a plain browser tab, not Electron) the callback URL is
 * the current origin's /auth/callback (Vercel rewrites it to the app HTML),
 * so Google OAuth + magic links return to the SAME tab. The desktop app
 * keeps the fixed loopback URL below.
 *
 * Compute is DELIBERATE (a call-time function, NOT a module-level constant):
 * the web shell self-declares (window.__PORTALS_WEB_SHELL__ — set by an
 * inline script in the web HTML pages and by the web entries) which takes
 * priority over EVERY other signal. A browser tab can therefore NEVER be
 * sent to the desktop's 127.0.0.1 loopback — even when the tab's user agent
 * smells like Electron (embedded app previews, branded browsers, extensions
 * that rewrite the UA). That exact mis-routing is what landed web logins on
 * the desktop's "Sign-in complete — return to Portals" page. The desktop
 * renderer, by contrast, declares nothing → the native preload bridge (or
 * UA fallback) selects the loopback, dev and packaged alike.
 */
export function getAuthRedirectUrl(): string {
  const LOOPBACK = "http://127.0.0.1:45123/auth/callback";
  if (typeof window === "undefined") return LOOPBACK;
  const origin = window.location.origin || "";
  const w = window as unknown as {
    __PORTALS_WEB_SHELL__?: boolean;
    glazeAPI?: { atlas?: unknown };
  };
  // WEB SHELL — self-declared. Always round-trip to this tab's own origin
  // (/auth/callback), whatever the UA says. The only escape is a non-http(s)
  // origin (file:// previews), which never serves the web app.
  if (w.__PORTALS_WEB_SHELL__) {
    return /^https?:\/\//.test(origin) ? `${origin}/auth/callback` : LOOPBACK;
  }
  // Real Electron desktop — the native preload bridge is the truth (stronger
  // than the UA string: it only exists in the packaged/dev desktop renderer).
  if (w.glazeAPI?.atlas) return LOOPBACK;
  // Final fallback signals for anything that set up neither marker nor bridge.
  const isElectron =
    typeof navigator !== "undefined" && /Electron/i.test(navigator.userAgent);
  if (isElectron) return LOOPBACK;
  return /^https?:\/\//.test(origin) ? `${origin}/auth/callback` : LOOPBACK;
}

export interface AuthUser {
  id: string;
  email: string | null;
  name: string | null;
  avatarUrl: string | null;
}

/**
 * Extract the user's public profile from the current session (null if none).
 * Anonymous sessions (the local-first background sign-in used for cloud sync)
 * are NOT real accounts — they return null so the login gate stays up.
 */
export async function getAuthUser(): Promise<AuthUser | null> {
  const client = getSupabase();
  if (!client) return null;
  const { data } = await client.auth.getSession();
  const user = data.session?.user;
  if (!user || user.is_anonymous) return null;
  const meta = user.user_metadata ?? {};
  // Google's avatar can land in identities[].identity_data when user_metadata
  // is sparse — fall through to it so the account button always has a photo.
  const identityMeta = (user.identities?.[0]?.identity_data ?? {}) as Record<string, unknown>;
  return {
    id: user.id,
    email: user.email ?? null,
    name:
      (meta.full_name as string) ??
      (meta.name as string) ??
      (identityMeta.full_name as string) ??
      (identityMeta.name as string) ??
      user.email ??
      null,
    avatarUrl:
      (meta.avatar_url as string) ??
      (meta.picture as string) ??
      (identityMeta.avatar_url as string) ??
      (identityMeta.picture as string) ??
      null,
  };
}

/**
 * The workspace OWNER's Supabase user id — the account that runs the
 * desktop app and owns the live rows (2026-09-02). Beta testers sign in
 * with their own Google accounts on the SAME project, so comparing the
 * signed-in user id cleanly separates owner-only features (e.g. the
 * keyless Pollinations image lane) from what beta users get (FAL BYOK;
 * hosted lanes come later).
 */
export const PORTALS_OWNER_USER_ID = "9878b74a-d5c0-4fbc-b031-b8f949a58c56";

/** True when the signed-in account IS the workspace owner. Returns false for
 *  anonymous / signed-out / any other account — callers on the DESKTOP
 *  (which has no other users) gate on the web flag instead of this. */
export async function isPortalsOwnerAccount(): Promise<boolean> {
  const user = await getAuthUser();
  if (!user) return false;
  return user.id === PORTALS_OWNER_USER_ID;
}

/**
 * Kick off Google OAuth: builds the authorize URL (PKCE challenge baked
 * in, verifier stored) and opens it in the system browser. The flow
 * completes when the browser lands back on `portals://auth/callback` —
 * handled by `completeAuthCallback`.
 */
export async function startGoogleLogin(): Promise<{ url: string }> {
  const client = getSupabase();
  if (!client) throw new Error("Supabase is not configured");
  const { data, error } = await client.auth.signInWithOAuth({
    provider: "google",
    options: {
      redirectTo: getAuthRedirectUrl(),
      skipBrowserRedirect: true,
      queryParams: { access_type: "offline" },
    },
  });
  if (error) throw error;
  if (!data.url) throw new Error("No OAuth URL returned");
  return { url: data.url };
}

/**
 * Magic-link email login: Supabase emails a link; clicking it opens the
 * browser which redirects to `portals://auth/callback?token_hash=…`.
 * First-time emails create the account (sign-up by magic link).
 */
export async function startEmailLogin(email: string): Promise<void> {
  const client = getSupabase();
  if (!client) throw new Error("Supabase is not configured");
  const { error } = await client.auth.signInWithOtp({
    email,
    options: { emailRedirectTo: getAuthRedirectUrl() },
  });
  if (error) throw error;
}

/**
 * Handle the deep-link callback URL. Handles both OAuth (code, PKCE) and
 * magic-link (token_hash) callbacks. Returns the signed-in user, or null
 * when Supabase is disabled.
 */
export async function completeAuthCallback(url: string): Promise<AuthUser | null> {
  const client = getSupabase();
  if (!client) return null;
  const params = new URL(url).searchParams;
  const code = params.get("code");
  const tokenHash = params.get("token_hash");
  if (code) {
    const flowId = params.get("sb_flow_id") ?? undefined;
    const { error } = await client.auth.exchangeCodeForSession(code, flowId ? { flowId } : undefined);
    if (error) throw error;
  } else if (tokenHash) {
    const type = (params.get("type") ?? "email") as "email" | "sms" | "phone" | "magiclink" | "recovery" | "invite" | "email_change";
    const { error } = await client.auth.verifyOtp({ token_hash: tokenHash, type });
    if (error) throw error;
  } else {
    throw new Error("Auth callback contained no code or token");
  }
  return getAuthUser();
}

/** Sign the current user out (clears the persisted session). */
export async function signOut(): Promise<void> {
  // An explicit sign-out must NOT be undone by the boot session rescue.
  cancelBootRescue();
  const client = getSupabase();
  if (!client) return;
  const { error } = await client.auth.signOut();
  if (error) console.warn("[supabase] sign-out failed:", error.message);
}

// ─────────────────────────────────────────────────────────────────────────────
// Schema constants — keep in sync with supabase/schema.sql
// ─────────────────────────────────────────────────────────────────────────────

export const SUPABASE_TABLES = {
  frames: "frames",
  frameVersions: "frame_versions",
} as const;
